import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, Outlet } from "react-router";

import { useMe } from "@/app/auth";
import { PageHeader } from "@/components/common/PageHeader";
import { cn } from "@/lib/utils";

export function SettingsLayout() {
  const { t } = useTranslation();
  const me = useMe();
  const tabs = [
    { to: "profile", label: t("settings.tabs.profile"), show: !me.mfa_setup_required },
    { to: "security", label: t("settings.tabs.security"), show: true },
    {
      to: "company",
      label: t("settings.tabs.company"),
      show: me.permissions.manage_company && !me.mfa_setup_required,
    },
    {
      to: "monitoring",
      label: t("settings.tabs.monitoring"),
      show: me.permissions.view_team && !me.mfa_setup_required,
    },
    {
      to: "categories",
      label: t("settings.tabs.categories"),
      show: me.permissions.view_team && !me.mfa_setup_required,
    },
    {
      to: "server",
      label: t("settings.tabs.server"),
      show: me.permissions.manage_company && !me.mfa_setup_required,
    },
    {
      to: "releases",
      label: t("settings.tabs.releases"),
      show: me.permissions.manage_company && !me.mfa_setup_required,
    },
    {
      to: "audit",
      label: t("settings.tabs.audit"),
      show: me.permissions.view_audit_log && !me.mfa_setup_required,
    },
  ].filter((tab) => tab.show);

  return (
    <>
      <PageHeader title={t("settings.title")} />
      <nav aria-label={t("settings.title")} className="mb-6 flex gap-1 overflow-x-auto border-b">
        {tabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            className={({ isActive }) =>
              cn(
                "-mb-px border-b-2 px-3 py-2 font-medium whitespace-nowrap transition-colors",
                isActive ? "border-primary text-primary" : "border-transparent text-muted hover:text-fg",
              )
            }
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>
      <Outlet />
    </>
  );
}

/** A titled section of a settings page. */
export function Section({
  title,
  description,
  children,
  aside,
}: {
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <section className="grid gap-4 border-b py-6 first:pt-0 last:border-b-0 lg:grid-cols-[280px_1fr] lg:gap-10">
      <div>
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold">{title}</h2>
          {aside}
        </div>
        {description && <p className="mt-1 text-muted">{description}</p>}
      </div>
      <div className="max-w-2xl min-w-0">{children}</div>
    </section>
  );
}
